import React from 'react';
import {useAppDispatch, useAppSelector} from "../../../redux/store";
import {
    selectCategory,
    selectCountProd,
    selectFilter,
    selectFilterTitle,
    selectFilterTitleSpm,
    selectTableContent,
    selectTableContentForSpm,
    selectTime,
    selectZoomedTime
} from "../../../common/selectors";
import {DataTableResType, DataTableSpmResType} from "../../../common/types";
import dayjs from "dayjs";
import {zoomAC} from "../../../redux/optionsReducer";
import {formatArrayTime, periodLineChart, valueOf} from "../../../common/optionsForDate";
import ReactApexChart from "react-apexcharts";
import {EmptyComponent} from "../../../common/empty";
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {wordsForLighting, wordsForNotLighting} from "../../../common/optionsForTimeLineChartAccent";

dayjs.extend(utc)
dayjs.extend(timezone)

// Компонент графика временных интервалов акций/скидок по маркетплейсам и супермаркетам
export const TimeLineChart1 = () => {
    // Получаем выбранную категорию (marketplaces/supermarkets)
    const category = useAppSelector(selectCategory)
    // Получаем данные для таблицы маркетплейсов
    const tableContent = useAppSelector(selectTableContent)
    // Получаем данные для таблицы супермаркетов
    const tableContentSpm = useAppSelector(selectTableContentForSpm)
    // Получаем выбранный временной диапазон
    const time = useAppSelector(selectTime)
    // Получаем фильтр
    const filter = useAppSelector(selectFilter)
    // Получаем выбранные маркетплейсы
    const filterTitleMps = useAppSelector(selectFilterTitle)
    // Получаем выбранные супермаркеты
    const filterTitleSpm = useAppSelector(selectFilterTitleSpm)
    // Получаем количество товаров
    const countProd = useAppSelector(selectCountProd)
    // Получаем временной диапазон для зума
    const zoom = useAppSelector(selectZoomedTime)
    // Получаем dispatch для работы с redux
    const dispatch = useAppDispatch()
    // Получаем временной диапазон для зума (повтор)
    const zoomedTime = useAppSelector(selectZoomedTime)

    // Массив данных для графика
    let data1: DataTableResType[] | DataTableSpmResType = []

    // Формируем данные для графика по выбранной категории
    if (category === 'marketplaces') {
        data1 = tableContent.map((el) => {
            return  {...el, to_date: dayjs(el.to_date).valueOf() + 86399000, get_date_row: dayjs(el.get_date_row).valueOf()}
        })
    }
    if (category === 'supermarkets') {
        data1 = tableContentSpm.map((el) => {
            return  {...el, to_date: dayjs(el.to_date).valueOf() + 86399000, get_date_row: dayjs(el.get_date_row).valueOf()}
        })
    }

    // Получаем уникальные названия площадок
    const namesPlaces: string[] = Array.from(new Set(data1.map((el) => el.spm)))
    // Формируем массив данных по площадкам
    const separateData: {name: string, data: DataTableResType[]}[] = namesPlaces.map((el) => {
        return {
            name: el,
            data: []
        }
    })

    // Заполняем массив данных по площадкам
    data1.forEach((el) => {
        separateData.forEach((item) => {
            return el.spm === item.name ? item.data.push(el) : item
        })
    })

    // Форматирование чисел в K/M
    const formatMandK = (value: number) => {
        if (value! <= 999999 && value! !== 0) {
            return (value!/1000)?.toFixed(1) + 'K'
        }
        if (value! > 999999 && value! !== 0) {
            return (value!/1000000)?.toFixed(1) + 'M'
        }
        return 0
    }

    // Формирование данных для графика
    const getData = () => {
        let dataForCharts:any = []
        let dataPlc: any[] = []
        separateData.length&&separateData.map((el) => {
            if (el.data.length === 1) {
                dataForCharts.push([el.data])
            } else {
                // Сортируем по дате начала, затем по дате окончания
                const sortedData = [...el.data].sort((a, b) => {
                    const aStart = Number(a.get_date_row);
                    const bStart = Number(b.get_date_row);
                    const aEnd = Number(a.to_date);
                    const bEnd = Number(b.to_date);
                    if (aStart !== bStart) {
                        return aStart - bStart;
                    }
                    return aEnd - bEnd;
                });

                // Массив строк, каждая строка содержит акции, которые не пересекаются
                const rows: DataTableResType[][] = [];

                // Для каждой акции ищем первую строку, где она помещается
                sortedData.forEach((item) => {
                    let placed = false;
                    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                        const row = rows[rowIndex];
                        // Проверяем, можно ли разместить акцию в этой строке
                        // Акция помещается, если её начало >= окончания последней акции в строке
                        const lastItemInRow = row[row.length - 1];
                        if (Number(item.get_date_row) >= Number(lastItemInRow.to_date)) {
                            row.push(item);
                            placed = true;
                            break;
                        }
                    }
                    // Если не нашли подходящую строку, создаём новую
                    if (!placed) {
                        rows.push([item]);
                    }
                });

                dataForCharts.push(rows);
            }

        })

        // Функция для определения цвета
        const getColor = (item: DataTableResType) => {
            const hasLightingWords = wordsForLighting.some(word =>
                item.acronym?.toLowerCase().includes(word) ||
                item.name_sale.toLowerCase().includes(word)
            );
            const hasNotLightingWords = wordsForNotLighting.some(word =>
                item.acronym?.toLowerCase().includes(word) ||
                item.name_sale.toLowerCase().includes(word) || item.spm === 'Самокат'
            );
            const hasNumbers = /\d/.test(item.average_percent.toString());

            if (hasLightingWords && hasNotLightingWords) {
                return item.colors;
            } else if (hasLightingWords) {
                return item.altColor;
            } else if (hasNotLightingWords) {
                return item.colors;
            } else if (!hasNumbers) {
                return item.altColor;
            } else {
                return item.colors;
            }
        };

        // Заполнение данных для графика по площадкам
        dataForCharts&&dataForCharts.forEach((data:any) => {
            data&&data.forEach((el:DataTableResType[], index: number) => {
                el.forEach((item) => dataPlc.push(category==='marketplaces'?
                    {
                        name: item.acronym ? item.acronym : item.name_sale,
                        color: !item.luminance ? item.colors : item.altColor,
                        place: item.spm,
                        data: [
                            {
                                x: `${item.spm}${index}`,
                                y: [
                                    item.get_date_row < dayjs(zoomedTime[0]).day(1).valueOf() ? dayjs(zoomedTime[0]).day(1).valueOf() + 10800000 : +item.get_date_row + 10800000,
                                    item.to_date > dayjs(zoomedTime[1]).day(7).valueOf()+86399000 ? dayjs(zoomedTime[1]).day(7).valueOf()+86399000 + 1080000 : +item.to_date + 10800000 /*+ 86399000*/,
                                ],
                                name: item.name_sale,
                                color: item.colors,
                                place: item.spm,
                                id: item.id,
                                count_categories: item.count_categories,
                                count_products: formatMandK(item.count_products!),
                                average_percent: item.average_percent,
                                get_date_row: item.get_date_row,
                                to_date: item.to_date,
                                acronym: item.acronym,
                                sec_info: item.sec_info

                            }
                        ]
                    }:
                    {
                        name: item.name_sale,
                        color: !item.luminance ? item.colors : item.altColor,
                        place: item.spm,
                        data: [
                            {
                                x: `${item.spm}${index}`,
                                y: [
                                    item.get_date_row < dayjs(zoomedTime[0]).day(1).valueOf() ? dayjs(zoomedTime[0]).day(1).valueOf() + 10800000 : +item.get_date_row + 10800000,
                                    item.to_date > dayjs(zoomedTime[1]).day(7).valueOf()+86399000 ? dayjs(zoomedTime[1]).day(7).valueOf()+86399000 + 1080000 : +item.to_date + 10800000 /*+ 86399000*/,
                                ],
                                name: item.name_sale,
                                color: item.colors,
                                place: item.spm,
                                id: item.id,
                                count_categories: item.count_categories,
                                average_percent: item.average_percent,
                                get_date_row: item.get_date_row,
                                to_date: item.to_date,

                            }
                        ]
                    }
                ))
            })
        })
        return dataPlc
    }

    // Массив данных для графика, отфильтрованный по выбранным площадкам
    const dataTotal = getData()
        .filter((el) => filterTitleMps.includes(el.place))
    // Формируем временной диапазон
    const formTime = valueOf(zoom)
    // Количество строк графика
    const countStringCharts = Array.from(new Set(dataTotal.map((el) => el.data[0].x))).length

    //_________________________________________________________________________________________________
    // Опции для графика ApexCharts
    const options = {
        chart: {
            //height: '1000px',
            //width: '100%',
            type: 'rangeBar' as const,
            foreColor: '#F5F5F5',
            toolbar: {
                show: false,
                offsetY: -10
            },
            offsetX: 0,
            zoom: {
                enabled: false
            },
            events: {
                zoomed: (chartContext: any, {xaxis, yaxis}: any) => {
                    return dispatch(zoomAC(xaxis.min?formatArrayTime(xaxis):zoomedTime))
                },
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '80%',
                rangeBarGroupRows: true,
                dataLabels: {
                    position: 'bottom',
                    hideOverflowingLabels: true,
                }
            }
        },
        dataLabels: {
            enabled: true,
            textAnchor: 'start' as const,
            offsetX: -40,
            formatter: (val: any, { seriesIndex, dataPointIndex, w }: any) => {
                let viewIndex: any = []
                w.globals.initialSeries.forEach((el: any, index: number) => {
                    if (!(el.data[0].y[1] < formTime[0] || el.data[0].y[0] > formTime[1])){
                        viewIndex.push(index)
                    }
                })
                let label = w.globals.seriesNames[seriesIndex]
                return  viewIndex.includes(seriesIndex) ? label : ''
            },
            style: {
                fontSize: '12px',
            }
        },
        xaxis: {
            show: false,
            min: periodLineChart(zoom)[0], // + 3 часа
            max: periodLineChart(zoom)[1], // + 1 день + 3 часа
            type: 'datetime' as const,
            position: 'top',
            labels: {
                show: false,
                style: {
                    colors: 'white',


                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            show: false,

        },
        stroke: {
            width: 1,
            colors: ['gray'],
        },
        grid: {
            show: true,
            borderColor: '#202020',
            xaxis: {
                lines: {
                    show: false
                }
            },
            yaxis: {
                lines: {
                    show: false
                }
            },
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: -15
            },
        },
        fill: {
            type: 'solid',
            opacity: 0.6
        },
        legend: {
            show: false
        },
        tooltip: {
            theme: 'dark',
            fillSeriesColor: true,
            custom: function({series, seriesIndex, dataPointIndex, w}: any) {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
                if (category === 'marketplaces') {
                    return `<div class="toolTip">` +
                        '<div class="toolTip__head">' + data.name + '</div>' +
                        '<div class="toolTip__list">'+
                        '<div style="color:'+data.color+'">'+ data.place +'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Количество товаров: '+'</span>'+data.count_products+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Анонсируемая скидка: '+'</span>'+data.average_percent+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Дата начала: '+'</span>'+dayjs(data.get_date_row).format('YYYY-MM-DD')+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Дата завершения: '+'</span>'+dayjs(data.to_date).format('YYYY-MM-DD')+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Описание: '+'</span>'+data.sec_info+'</div>'+
                        '</div>'+
                        '<div/>'
                } else {
                    return `<div class="toolTip">` +
                        '<div class="toolTip__head">' + data.name + '</div>' +
                        '<div class="toolTip__list">'+
                        '<div style="color:'+data.color+'">'+ data.place +'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Категории(SKU): '+'</span>'+data.count_categories+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Размер скидки: '+'</span>'+data.average_percent+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Дата начала: '+'</span>'+dayjs(data.get_date_row).format('YYYY-MM-DD')+'</div>'+
                        '<div>'+'<span class="toolTip__item">'+'Дата завершения: '+'</span>'+dayjs(data.to_date).format('YYYY-MM-DD')+'</div>'+
                        '</div>'+
                        '<div/>'
                }
            },
        },
    }
    // ...рендер...
    return (
        <div>
            {/* График временных интервалов или заглушка */}
            {dataTotal.length?<ReactApexChart options={options}
                                              series={dataTotal}
                                              type="rangeBar"
                                              height={20*countStringCharts + 50}
                                              width={1000}
                                              style={{backgroundColor: '#202020', textColor: 'white'}}/>:<EmptyComponent/>}
        </div>
    );
};

